// Helpers visuels des nœuds ECA (icône + famille de couleur).
//
// Config-first : aucun rjBuild reactive-json ne peut rendre un canvas ReactFlow
// éditable (les `nodeTypes` sont des composants React imposés par la lib), et ce
// module n'ajoute pas d'UI — c'est un helper PUR (aucun JSX) de l'éditeur ECA
// existant, isolé dans un `.js` pour ne pas casser le Fast Refresh des `.jsx`.
//
// Il ne connaît QUE des identifiants de plugins ECA (générique, réutilisable) :
// aucune règle spécifique à un flux Orbit.

import {
  BookOpen, Braces, ClipboardList, Clock, CircleCheck, CircleHelp, ExternalLink,
  Eye, FilePlus2, GitBranch, GitMerge, Globe, Hash, KeyRound, Mail, Play,
  RefreshCw, Repeat, Save, Scale, ScrollText, ShieldCheck, SquarePen, Tag,
  Trash2, UserRound, Zap,
} from 'lucide-react';

/** Préfixe des actions dérivées d'une CollectionExport eca_rest.
 *  Cf. eca_rest/src/Plugin/Action/RestExport.php (id du plugin de base). */
export const REST_ACTION_PREFIX = 'eca_rest_collection_export:';

/** Une action ECA REST (appel HTTP sortant) — signalée en orange dans l'éditeur. */
export const isRestActionPlugin = (plugin) =>
  typeof plugin === 'string' && plugin.startsWith(REST_ACTION_PREFIX);

/** Clé de NODE_COLORS à utiliser pour un nœud donné. */
export const nodeColorKey = (type, plugin) =>
  type === 'action' && isRestActionPlugin(plugin) ? 'restAction' : type;

// --- Règles icône, du plus spécifique au plus générique -----------------------
// Chaque entrée : [motif testé sur l'id de plugin, composant lucide].

const EVENT_ICONS = [
  [/presave/, Save],
  [/insert|create/, FilePlus2],
  [/update|revision/, RefreshCw],
  [/delete/, Trash2],
  [/view|load/, Eye],
  [/form/, ClipboardList],
  [/user|account|login|logout/, UserRound],
  [/cron/, Clock],
];

const CONDITION_ICONS = [
  [/empty/, CircleHelp],
  [/exists/, CircleCheck],
  [/count/, Hash],
  [/role|permission|access/, ShieldCheck],
  [/moderation|state|workflow/, GitBranch],
  [/compar|equals|greater|lower|scalar/, Scale],
];

const ACTION_ICONS = [
  [/^eca_rest/, Globe],
  [/json|serialize/, Braces],
  [/key/, KeyRound],
  [/log|message/, ScrollText],
  [/mail/, Mail],
  [/set_field_value|write|save/, SquarePen],
  [/get_field_value|read/, BookOpen],
  [/token/, Tag],
  [/list|loop|iterate|each/, Repeat],
  [/new_entity|entity_create/, FilePlus2],
  [/delete/, Trash2],
  [/redirect|route|url/, ExternalLink],
];

const RULES_BY_TYPE = {
  event: EVENT_ICONS,
  condition: CONDITION_ICONS,
  action: ACTION_ICONS,
};

const FALLBACK_BY_TYPE = {
  event: Zap,
  condition: CircleHelp,
  action: Play,
  gateway: GitMerge,
};

/**
 * Icône lucide d'un nœud, déduite de son type et de son id de plugin.
 * Retourne toujours un composant (jamais null) — l'appelant peut le rendre tel quel.
 */
export const getNodeIcon = (type, plugin) => {
  const id = typeof plugin === 'string' ? plugin.toLowerCase() : '';
  const rule = (RULES_BY_TYPE[type] ?? []).find(([pattern]) => pattern.test(id));
  return rule ? rule[1] : (FALLBACK_BY_TYPE[type] ?? Play);
};
